/**
 * Automation Status Card Component
 * 
 * Dashboard card showing overall automation status for staff.
 * Part of the 90% Automation System visibility UI.
 */

import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';

interface AutomationStats {
  total_cases: number;
  ready_cases: number;
  auto_ready_cases: number;
  blocked_cases: number;
  pending_action_items: number;
  avg_case_strength: number;
}

interface AutomationStatusCardProps {
  className?: string;
}

export const AutomationStatusCard: React.FC<AutomationStatusCardProps> = ({
  className = ''
}) => {
  const [stats, setStats] = useState<AutomationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/api/v1/attorney-ai/dashboard-stats');
        const data = response.data || response;
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch automation stats:', err);
        // Set default stats if endpoint doesn't exist yet
        setStats({
          total_cases: 0,
          ready_cases: 0,
          auto_ready_cases: 0,
          blocked_cases: 0,
          pending_action_items: 0,
          avg_case_strength: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse ${className}`}>
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const readyPercentage = stats.total_cases > 0 
    ? Math.round((stats.ready_cases / stats.total_cases) * 100) 
    : 0;

  return (
    <div className={`bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-sm border border-blue-100 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Automation Status</h3>
            <p className="text-xs text-gray-500">90% Automation System</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{readyPercentage}%</div>
          <div className="text-xs text-gray-500">Cases Ready</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Ready */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.ready_cases}</div>
          <div className="text-xs text-gray-600">Ready</div>
        </div>

        {/* Auto-Ready */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-emerald-600">{stats.auto_ready_cases}</div>
          <div className="text-xs text-gray-600">Auto-Ready</div>
        </div>

        {/* Blocked */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.blocked_cases}</div>
          <div className="text-xs text-gray-600">Blocked</div>
        </div>

        {/* Pending Items */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.pending_action_items}</div>
          <div className="text-xs text-gray-600">Pending Items</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Case Readiness Progress</span>
          <span>{stats.ready_cases} / {stats.total_cases}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full flex">
            <div 
              className="bg-emerald-500 transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? (stats.auto_ready_cases / stats.total_cases) * 100 : 0}%` }}
            />
            <div 
              className="bg-green-500 transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? ((stats.ready_cases - stats.auto_ready_cases) / stats.total_cases) * 100 : 0}%` }}
            />
            <div 
              className="bg-red-500 transition-all duration-500"
              style={{ width: `${stats.total_cases > 0 ? (stats.blocked_cases / stats.total_cases) * 100 : 0}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between mt-1">
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Auto-Ready
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Ready
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" /> Blocked
            </span>
          </div>
        </div>
      </div>

      {/* Avg Strength */}
      <div className="mt-4 pt-4 border-t border-blue-100">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Average Case Strength</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  stats.avg_case_strength >= 90 ? 'bg-green-500' :
                  stats.avg_case_strength >= 70 ? 'bg-blue-500' :
                  stats.avg_case_strength >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${stats.avg_case_strength}%` }}
              />
            </div>
            <span className="font-bold text-gray-900">{Math.round(stats.avg_case_strength)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationStatusCard;
